import * as React from 'react';

import cn from 'clsx';
import { useSelector } from 'react-redux';
import sanitize from 'sanitize-html';
import { ERuntimeSettings } from 'shared/types/runtimeSettings';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { SeoMarkedList } from '../../components/SeoMarkedList';
import { ETestIds } from '../../constants/testIds';
import { useIsDesktop } from '../../hooks/common';
import { selectCalculatorErrors, selectMarkedListItems } from '../../selectors/landing';
import { FillApplicationButton } from '../stickyButtons/FillApplicationButton';
import { EFillApplicationStickyButtonType } from '../stickyButtons/FillApplicationButton/types';
import { DefaultSeoText } from './components/DefaultSeoText';
import { RedesignDefaultSeoText } from './components/RedesignDefaultSeoText';
import { SEOLinksBlock } from './components/SEOLinksBlock';
import * as styles from './styles.css';
import { useSanitizedRichText } from './utils';

export interface IInformationalSeoBlockProps {
  offersLength: number;
}

export const InformationalSeoBlock: React.FC<IInformationalSeoBlockProps> = ({ offersLength }) => {
  const isDesktop = useIsDesktop();
  const calculatorErrors = useSelector(selectCalculatorErrors);
  const isMasterButtonShows = isDesktop && offersLength > 0 && calculatorErrors.length === 0;
  const seoTextItems = useSelector(selectMarkedListItems);
  const sanitizedRichText = useSanitizedRichText();
  const isRichTextAvailable = sanitizedRichText;
  const { config } = useApplicationContext();
  const mortgageLandingRedesignEnabled = config.get<boolean>(ERuntimeSettings.mortgageLandingRedesign);
  const sanitizedItems = React.useMemo(
    () =>
      seoTextItems.map(item => ({
        ...item,
        title: sanitize(item.title),
        content: sanitize(item.content),
      })),
    [seoTextItems],
  );

  /* oxlint-disable react/no-danger */
  const renderRichTextBlock = () =>
    isRichTextAvailable ? (
      <div
        className={cn(styles['border-wrapper'], styles['mb16'])}
        data-testid={ETestIds.SeoRichText}
        dangerouslySetInnerHTML={{ __html: sanitizedRichText }}
      />
    ) : null;

  if (sanitizedItems.length !== 0) {
    return (
      <div className={styles['root']} data-testid={ETestIds.SeoMarkedList}>
        {isRichTextAvailable && renderRichTextBlock()}
        <SeoMarkedList items={sanitizedItems} />
      </div>
    );
  }

  return (
    <div
      className={cn(styles['root'], mortgageLandingRedesignEnabled && styles['root-redesign'])}
      data-testid={ETestIds.InformationalSeo}
    >
      {isRichTextAvailable ? (
        renderRichTextBlock()
      ) : mortgageLandingRedesignEnabled ? (
        <RedesignDefaultSeoText />
      ) : (
        <DefaultSeoText />
      )}
      {isMasterButtonShows && !mortgageLandingRedesignEnabled && (
        <FillApplicationButton currentButtonType={EFillApplicationStickyButtonType.BOTTOM} />
      )}
      {mortgageLandingRedesignEnabled && <SEOLinksBlock />}
    </div>
  );
};
